import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../components/Layout';
import { 
  Download, 
  Mail, 
  ExternalLink, 
  Award, 
  Newspaper,
  Camera,
  FileText,
  CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';

const Press = () => {
  const pressFeatures = [
    'First tap-to-share NFC business card platform in India',
    'Eco-friendly alternative to traditional paper cards',
    'Built-in CRM and analytics for professionals',
    'Serving doctors, lawyers, real estate agents, and entrepreneurs',
    'Premium NFC cards with custom designs',
    'Free digital business card creation',
  ];

  const mediaAssets = [
    { name: 'Logo (PNG)', file: '/logo512.png', size: '512x512' },
    { name: 'Logo (Small)', file: '/logo192.png', size: '192x192' },
    { name: 'Brand Icon', file: '/piper-logo-qr.png', size: '80x80' },
    { name: 'OG Image', file: '/piper-card-og.png', size: '1200x1200' },
  ];

  const pressContacts = [
    { 
      name: 'General Inquiries', 
      email: 'contact@allgrowtechnologies.com',
      description: 'For media, partnerships, and general inquiries'
    },
  ];

  return (
    <Layout>
      <Helmet>
        <title>Press & Media | Piper Card - India's Leading NFC Business Card Platform</title>
        <meta name="description" content="Press resources, media kit, and brand assets for Piper Card - India's leading NFC digital business card platform. Download logos, get company information, and contact our press team." />
        <meta name="keywords" content="Piper Card press, NFC business card India, digital business card startup, Piper Card media kit, business card innovation" />
        <link rel="canonical" href="https://pipercard.com/press" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Press & Media | Piper Card" />
        <meta property="og:description" content="Press resources and media kit for India's leading NFC digital business card platform." />
        <meta property="og:url" content="https://pipercard.com/press" />
        <meta property="og:type" content="website" />
        
        {/* Structured Data for Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Piper Card",
            "alternateName": "Piper Card by Allgrow Technologies",
            "url": "https://pipercard.com",
            "logo": "https://pipercard.com/logo512.png",
            "description": "India's leading NFC digital business card platform for modern professionals",
            "foundingDate": "2024",
            "foundingLocation": {
              "@type": "Place",
              "name": "India"
            },
            "contactPoint": [
              {
                "@type": "ContactPoint",
                "contactType": "Customer Service",
                "email": "contact@allgrowtechnologies.com"
              }
            ],
            "sameAs": [
              "https://www.facebook.com/profile.php?id=61588342580492",
              "https://www.instagram.com/pipercard/",
              "https://www.youtube.com/@smartpipercard",
              "https://twitter.com/pipercard"
            ],
            "areaServed": {
              "@type": "Country",
              "name": "India"
            },
            "knowsAbout": [
              "NFC Business Cards",
              "Digital Business Cards",
              "Professional Networking",
              "Contact Management",
              "QR Code Technology"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 text-amber-400 mb-4">
            <Newspaper className="w-5 h-5" />
            <span className="text-sm font-medium uppercase tracking-wide">Press & Media</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Piper Card Press Kit
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mb-8">
            Everything you need to write about India's leading NFC digital business card platform. 
            Download brand assets, company information, and get in touch with our press team.
          </p>
          <div className="flex flex-wrap gap-4">
            <a href="mailto:contact@allgrowtechnologies.com">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                <Mail className="w-4 h-4 mr-2" />
                Contact Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Company Overview */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">About Piper Card</h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Piper Card is India's pioneering NFC digital business card platform, revolutionizing 
                how professionals network and share contact information. Founded in 2024, we've helped 
                thousands of doctors, lawyers, entrepreneurs, and business professionals modernize 
                their networking with tap-to-share technology.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Our platform combines physical NFC cards with a powerful digital ecosystem featuring 
                customizable profiles, built-in CRM, analytics tracking, and instant contact sharing. 
                We're committed to making professional networking more efficient, eco-friendly, and 
                impactful.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Highlights</h3>
              <ul className="space-y-2">
                {pressFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Facts */}
            <div>
              <div className="bg-white rounded-xl p-6 shadow-sm border mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-500" />
                  Quick Facts
                </h3>
                <dl className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <dt className="text-gray-500">Founded</dt>
                    <dd className="font-medium text-gray-900">2024</dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <dt className="text-gray-500">Headquarters</dt>
                    <dd className="font-medium text-gray-900">India</dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <dt className="text-gray-500">Industry</dt>
                    <dd className="font-medium text-gray-900">Business Networking / SaaS</dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <dt className="text-gray-500">Products</dt>
                    <dd className="font-medium text-gray-900">NFC Cards, Digital Profiles</dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <dt className="text-gray-500">Target Market</dt>
                    <dd className="font-medium text-gray-900">Professionals, SMBs</dd>
                  </div>
                  <div className="flex justify-between py-2">
                    <dt className="text-gray-500">Website</dt>
                    <dd className="font-medium text-amber-600">pipercard.com</dd>
                  </div>
                </dl>
              </div>

              {/* Parent Company */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Parent Company</h3>
                <p className="text-gray-600 mb-2">
                  <strong>Allgrow Technologies</strong>
                </p>
                <p className="text-sm text-gray-500">
                  Allgrow Technologies is a technology company focused on building innovative 
                  solutions for modern businesses. Piper Card is our flagship product in the 
                  professional networking space.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Media Assets */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 text-amber-600 mb-2">
              <Camera className="w-5 h-5" />
              <span className="text-sm font-medium uppercase tracking-wide">Brand Assets</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Media Kit & Logos</h2>
            <p className="text-gray-500">Download official Piper Card logos and brand assets</p>
          </div>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {mediaAssets.map((asset, index) => (
              <a 
                key={index}
                href={asset.file}
                download
                className="bg-white rounded-xl p-6 border hover:border-amber-400 hover:shadow-md transition-all group text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-amber-50">
                  <img src={asset.file} alt={asset.name} className="max-w-12 max-h-12 object-contain" />
                </div>
                <div className="font-medium text-gray-900">{asset.name}</div>
                <div className="text-sm text-gray-500">{asset.size}</div>
                <div className="mt-2 text-amber-600 text-sm flex items-center justify-center gap-1">
                  <Download className="w-3 h-3" />
                  Download
                </div>
              </a>
            ))}
          </div>

          <div className="bg-white rounded-xl p-6 border">
            <h3 className="font-semibold text-gray-900 mb-4">Brand Guidelines</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Primary Colors</h4>
                <div className="flex gap-2">
                  <div className="w-10 h-10 rounded-lg bg-amber-500" title="#F59E0B"></div>
                  <div className="w-10 h-10 rounded-lg bg-gray-900" title="#111827"></div>
                  <div className="w-10 h-10 rounded-lg bg-white border" title="#FFFFFF"></div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Typography</h4>
                <p className="text-sm text-gray-500">Primary: Inter, System UI<br/>Headings: Bold weight</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Logo Usage</h4>
                <p className="text-sm text-gray-500">Maintain clear space around logo. Do not stretch or alter colors.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Press Contact */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Press Contacts</h2>
            <p className="text-gray-500">Get in touch with our team</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {pressContacts.map((contact, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 border text-center">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{contact.name}</h3>
                <p className="text-sm text-gray-500 mb-3">{contact.description}</p>
                <a 
                  href={`mailto:${contact.email}`}
                  className="text-amber-600 hover:text-amber-700 font-medium text-sm"
                >
                  {contact.email}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Write About Piper Card?</h2>
          <p className="text-lg text-white/90 mb-8">
            We're happy to provide interviews, product demos, and additional information for your story.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="mailto:contact@allgrowtechnologies.com">
              <Button size="lg" className="bg-white text-amber-600 hover:bg-gray-100">
                <Mail className="w-4 h-4 mr-2" />
                Contact Us
              </Button>
            </a>
            <Link to="/shop">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <ExternalLink className="w-4 h-4 mr-2" />
                Explore Products
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Press;
